import type { PostHog } from "posthog-js";

/**
 * PostHog product analytics + session replay for the web app. Mirrors the existing imperative telemetry
 * pattern (Sentry in index.tsx): a singleton initialised once at bootstrap, with identify/reset hung off
 * the same points as Sentry.setUser.
 *
 * Env (resolved by the Vite REACT_APP_* define shim in vite.config.js):
 *   REACT_APP_POSTHOG_KEY   – project API key (integration is a no-op without it)
 *   REACT_APP_POSTHOG_HOST  – e.g. https://us.i.posthog.com | https://eu.i.posthog.com | self-host
 */

/**
 * The initialised client, once posthog-js has loaded. Unset when PostHog is off for this document
 * (unconfigured, Cypress, a share page), which makes identify/reset no-ops.
 *
 * posthog-js is loaded with a dynamic import: it is the largest library the app would otherwise load at
 * startup, and nothing needs it before the first paint. identify/reset chain on this promise, so a call
 * made before the library arrives is applied, in order, once it has initialised.
 */
let client: Promise<PostHog> | undefined;

const posthogKey = () => process.env.REACT_APP_POSTHOG_KEY;

export function initPostHog(): void {
  const key = posthogKey();
  // Skip when unconfigured or under Cypress, and never double-init.
  // `Cypress` is injected by the Cypress runner at runtime and has no ambient declaration in
  // this project; same erased form as Router.tsx l.288.
  if (!key || client || (globalThis as typeof globalThis & { Cypress?: unknown }).Cypress) return;
  // CP-283 recipient pages: never. Recording runs with inputs unmasked and autocapture sends the
  // token-bearing URL with every click — the same reason Sentry Replay is off there (index.tsx).
  // Share views always start as a full load on /share/..., so deciding at init time is complete.
  if (globalThis.location.pathname.startsWith("/share/")) return;

  client = import("posthog-js").then(({ default: posthog }) => {
    posthog.init(key, {
      api_host: process.env.REACT_APP_POSTHOG_HOST || "https://us.i.posthog.com",
      // Only create person profiles once we've identified a real user (keeps
      // anonymous/logged-out traffic cheap and avoids PII on the anon profile).
      person_profiles: "identified_only",
      // SPA pageviews (react-router v6) are captured automatically on history changes.
      capture_pageview: true,
      capture_pageleave: true,
      autocapture: true,
      disable_session_recording: false,
      session_recording: {
        // Capture everything (text + inputs) EXCEPT password fields, which stay
        // masked via maskInputOptions.password.
        maskAllInputs: false,
        maskInputOptions: { password: true },
      },
      loaded: (ph) => {
        if (process.env.REACT_APP_ENV !== "prod") ph.debug(false);
      },
    });
    return posthog;
  });
}

export interface PostHogUser {
  _id?: string | undefined;
  email?: string | undefined;
  /** Matches userDetails.portal (accounts.schema userDetailsMembership): the requesting portal's membership. */
  portal?:
    | {
        _id?: string | null | undefined;
        /** Plan code → seat type number, e.g. { CDE: 1, PM: 0 } */
        seat?: Record<string, number> | undefined;
        role?: { _id?: string | null | undefined; name?: string | undefined } | undefined;
      }
    | null
    | undefined;
}

export function identifyPostHog(user: PostHogUser | undefined): void {
  if (!user?._id) return;
  const { _id } = user;
  const role = user.portal?.role;
  void client?.then((posthog) => {
    posthog.identify(_id, {
      email: user.email,
      portalId: user.portal?._id,
      // The role's id and name only: the membership's role also holds its permission ids.
      role: role && { _id: role._id, name: role.name },
      seat: user.portal?.seat,
    });
  });
}

export function resetPostHog(): void {
  void client?.then((posthog) => {
    posthog.reset();
  });
}
